<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12">
                <v-card width="100%">
                    <v-card-title class="d-block py-2 text-center font-weight-bold primary secondary--text" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">mdi-cash-multiple</v-icon>
                        MONEDAS
                    </v-card-title>
                    <v-btn bottom color="secondary primary--text" :loading="loading" depressed fab absolute right small @click="getData">
                        <v-icon>mdi-sync</v-icon>
                    </v-btn>
                </v-card>
            </v-col>
        </v-row>

        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-text>
                        <v-row dense align="center">
                            <v-col cols="12" md="8">
                                <v-text-field
                                    v-model="search"
                                    append-icon="mdi-magnify"
                                    label="Buscar moneda"
                                    single-line
                                    hide-details/>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-btn block large color="primary" depressed :disabled="loading" @click="openDialog()">
                                    <v-icon left>mdi-plus-circle</v-icon>Agregar moneda
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-data-table
                        :headers="headers"
                        :items="currencies"
                        :search="search"
                        :loading="loading"
                        :footer-props="footerProps"
                        no-data-text="No hay monedas configuradas">
                        <template v-slot:item.code="{ item }">
                            <strong>{{ item.code }}</strong>
                        </template>
                        <template v-slot:item.symbol="{ item }">
                            <v-chip small label color="secondary" class="primary--text font-weight-bold">
                                {{ item.symbol }}
                            </v-chip>
                        </template>
                        <template v-slot:item.logo="{ item }">
                            <v-avatar size="34" color="secondary" class="primary--text font-weight-bold">
                                <img v-if="item.logo_url" :src="item.logo_url" :alt="item.code">
                                <span v-else>{{ item.symbol }}</span>
                            </v-avatar>
                        </template>
                        <template v-slot:item.type="{ item }">
                            {{ typeLabel(item.type) }}
                        </template>
                        <template v-slot:item.decimal_digits="{ item }">
                            {{ item.decimal_digits }}
                        </template>
                        <template v-slot:item.status="{ item }">
                            <v-chip small label :color="item.status ? 'success' : 'grey'" dark>
                                {{ item.status ? 'Activa' : 'Inactiva' }}
                            </v-chip>
                        </template>
                        <template v-slot:item.action="{ item }">
                            <v-btn small depressed color="primary" @click="openDialog(item)">
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="dialog" scrollable persistent max-width="760px">
            <v-card>
                <v-form ref="form" v-model="valid" @submit.prevent="submit" lazy-validation>
                    <v-card-title class="primary secondary--text" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">{{ form.id ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
                        {{ form.id ? 'Editar moneda' : 'Agregar moneda' }}
                        <v-spacer/>
                        <v-btn icon color="secondary" @click="closeDialog">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text class="pt-4">
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="form.code"
                                    label="Codigo"
                                    prepend-icon="mdi-pound"
                                    :rules="[rules.required('code')]"
                                    :error-messages="errors.code"
                                    :disabled="loading"
                                    @input="form.code = normalizeCode(form.code); clearErrors('code')"/>
                            </v-col>
                            <v-col cols="12" sm="8">
                                <v-text-field
                                    v-model="form.name"
                                    label="Nombre"
                                    prepend-icon="mdi-currency-sign"
                                    :rules="[rules.required('name')]"
                                    :error-messages="errors.name"
                                    :disabled="loading"
                                    @input="clearErrors('name')"/>
                            </v-col>
                            <v-col cols="12" sm="8">
                                <v-text-field
                                    v-model="form.name_plural"
                                    label="Nombre plural"
                                    prepend-icon="mdi-format-letter-case"
                                    :error-messages="errors.name_plural"
                                    :disabled="loading"
                                    @input="clearErrors('name_plural')"/>
                            </v-col>
                            <v-col cols="6" sm="2">
                                <v-text-field
                                    v-model="form.symbol"
                                    label="Simbolo"
                                    :rules="[rules.required('symbol')]"
                                    :error-messages="errors.symbol"
                                    :disabled="loading"
                                    @input="clearErrors('symbol')"/>
                            </v-col>
                            <v-col cols="6" sm="2">
                                <v-text-field
                                    v-model="form.symbol_native"
                                    label="Simbolo local"
                                    :error-messages="errors.symbol_native"
                                    :disabled="loading"
                                    @input="clearErrors('symbol_native')"/>
                            </v-col>
                            <v-col cols="12" sm="7">
                                <v-text-field
                                    v-model="form.logo_url"
                                    label="Logo URL o ruta"
                                    prepend-icon="mdi-image"
                                    :error-messages="errors['metadata.logo_url'] || errors.logo_url"
                                    :disabled="loading"
                                    @input="clearErrors('metadata.logo_url'); clearErrors('logo_url')"/>
                            </v-col>
                            <v-col cols="12" sm="5">
                                <v-file-input
                                    v-model="form.logo_file"
                                    label="Cargar logo"
                                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                    prepend-icon="mdi-upload"
                                    :error-messages="errors.logo_file"
                                    :disabled="loading"
                                    @change="clearErrors('logo_file')"/>
                            </v-col>
                            <v-col cols="12" v-if="currencyLogoPreview">
                                <v-sheet outlined rounded class="pa-3 d-flex align-center">
                                    <v-avatar size="42" color="secondary" class="primary--text font-weight-bold mr-3">
                                        <img v-if="currencyLogoPreview" :src="currencyLogoPreview" :alt="form.code || 'Logo'">
                                        <span v-else>{{ form.symbol }}</span>
                                    </v-avatar>
                                    <div>
                                        <div class="subtitle-2 font-weight-bold primary--text">Logo de la moneda</div>
                                        <div class="caption">Se usara en el centro del QR cuando aplique.</div>
                                    </div>
                                </v-sheet>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model.number="form.decimal_digits"
                                    label="Decimales"
                                    type="number"
                                    min="0"
                                    max="8"
                                    prepend-icon="mdi-decimal"
                                    :rules="[rules.required('decimal_digits')]"
                                    :error-messages="errors.decimal_digits"
                                    :disabled="loading"
                                    @input="clearErrors('decimal_digits')"/>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="form.type"
                                    :items="currencyTypes"
                                    item-text="text"
                                    item-value="value"
                                    label="Tipo"
                                    prepend-icon="mdi-tag"
                                    :rules="[rules.required('type')]"
                                    :error-messages="errors.type"
                                    :disabled="loading"
                                    @change="clearErrors('type')"/>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="form.status"
                                    :items="statusOptions"
                                    item-text="text"
                                    item-value="value"
                                    label="Estado"
                                    prepend-icon="mdi-check-circle"
                                    :rules="[]"
                                    :error-messages="errors.status"
                                    :disabled="loading"
                                    @change="clearErrors('status')"/>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model.number="form.sort_order"
                                    label="Orden"
                                    type="number"
                                    min="0"
                                    max="9999"
                                    prepend-icon="mdi-sort"
                                    :error-messages="errors.sort_order"
                                    :disabled="loading"
                                    @input="clearErrors('sort_order')"/>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-divider/>
                    <v-card-actions>
                        <v-btn text color="error" @click="closeDialog">
                            <v-icon left>mdi-close-circle</v-icon>Cerrar
                        </v-btn>
                        <v-spacer/>
                        <v-btn type="submit" color="success" :loading="loading" :disabled="loading || !valid">
                            <v-icon left>mdi-check-circle</v-icon>Guardar
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';
import Form from "../../../helpers/form";

export default {
    name: "configCurrencies",
    mixins: [Form],
    data: () => ({
        loading: false,
        dialog: false,
        valid: true,
        search: '',
        currencies: [],
        footerProps: {
            'items-per-page-options': [15, 30, 50, -1],
            'items-per-page-text': 'Monedas por pagina'
        },
        headers: [
            { text: 'Codigo', value: 'code' },
            { text: 'Nombre', value: 'name' },
            { text: 'Simbolo', value: 'symbol' },
            { text: 'Logo', value: 'logo', sortable: false },
            { text: 'Tipo', value: 'type' },
            { text: 'Decimales', value: 'decimal_digits' },
            { text: 'Estado', value: 'status' },
            { text: 'Orden', value: 'sort_order' },
            { text: '', value: 'action', sortable: false, align: 'end' }
        ],
        currencyTypes: [
            { text: 'Fiat', value: 'fiat' },
            { text: 'Stablecoin', value: 'stablecoin' },
            { text: 'Cripto', value: 'crypto' },
            { text: 'Otro', value: 'other' }
        ],
        statusOptions: [
            { text: 'Activa', value: true },
            { text: 'Inactiva', value: false }
        ],
        form: {
            id: null,
            code: '',
            name: '',
            name_plural: '',
            symbol: '',
            symbol_native: '',
            logo_url: '',
            logo_file: null,
            decimal_digits: 2,
            type: 'fiat',
            status: true,
            sort_order: 100,
            metadata: {
                logo_url: ''
            }
        }
    }),
    computed: {
        currencyLogoPreview() {
            if (this.form.logo_file) {
                return URL.createObjectURL(this.form.logo_file);
            }

            return this.form.logo_url || '';
        }
    },
    created() {
        this.getData();
    },
    methods: {
        getData() {
            this.loading = true;
            axios.get('/api/settings/currencies').then(res => {
                this.currencies = res.data.currencies || [];
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible cargar monedas.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        openDialog(item = null) {
            this.clearErrors();

            if (item) {
                this.form = {
                    id: item.id,
                    code: item.code,
                    name: item.name,
                    name_plural: item.name_plural,
                    symbol: item.raw_symbol || item.symbol,
                    symbol_native: item.raw_symbol_native || item.symbol_native || item.symbol,
                    logo_url: item.logo_url || (item.metadata && item.metadata.logo_url ? item.metadata.logo_url : ''),
                    logo_file: null,
                    decimal_digits: item.decimal_digits,
                    type: item.type || 'fiat',
                    status: item.status !== false,
                    sort_order: item.sort_order || 100,
                    metadata: Object.assign({}, item.metadata || {}, {
                        logo_url: item.logo_url || (item.metadata && item.metadata.logo_url ? item.metadata.logo_url : '')
                    })
                };
            } else {
                this.form = {
                    id: null,
                    code: '',
                    name: '',
                    name_plural: '',
                    symbol: '',
                    symbol_native: '',
                    logo_url: '',
                    logo_file: null,
                    decimal_digits: 2,
                    type: 'fiat',
                    status: true,
                    sort_order: 100,
                    metadata: {
                        logo_url: ''
                    }
                };
            }

            this.dialog = true;
        },
        closeDialog() {
            this.dialog = false;
        },
        submit() {
            if (!this.$refs.form.validate()) return;

            const payload = this.currencyPayload();
            const url = this.form.id ? '/api/settings/currencies/' + this.form.id : '/api/settings/currencies';

            this.loading = true;
            axios.post(url, payload).then(res => {
                this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                this.currencies = res.data.currencies || this.currencies;
                this.closeDialog();
            }).catch(err => {
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                this.$toast.error(this.errorMessage(err, 'No fue posible guardar la moneda.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        normalizeCode(value) {
            return String(value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10);
        },
        currencyPayload() {
            const data = new FormData();
            data.append('code', this.normalizeCode(this.form.code));
            data.append('name', this.form.name || '');
            data.append('name_plural', this.form.name_plural || this.form.name || '');
            data.append('symbol', this.form.symbol || '');
            data.append('symbol_native', this.form.symbol_native || this.form.symbol || '');
            data.append('decimal_digits', this.form.decimal_digits);
            data.append('type', this.form.type || 'fiat');
            data.append('status', this.form.status ? 1 : 0);
            data.append('sort_order', this.form.sort_order || 100);
            data.append('metadata[logo_url]', this.form.logo_url || '');

            if (this.form.logo_file) {
                data.append('logo_file', this.form.logo_file);
            }

            return data;
        },
        typeLabel(value) {
            const item = this.currencyTypes.find(type => type.value === value);
            return item ? item.text : value;
        },
        errorMessage(err, fallback) {
            if (err.response && err.response.data && err.response.data.status) {
                return err.response.data.status;
            }

            return fallback;
        }
    }
}
</script>
